import config from '../../../config';
import routeConfiguration from '../../../routeConfiguration';
import { matchPathname } from '../../../util/routes';

const INTERNAL_ORIGIN = /^https?:\/\/(www\.)?vansite\.eu/i;

/**
 * Re-localize an internal app URL coming from a Storyblok `url` multilink.
 *
 * Editors paste production URLs with a hard-coded locale (e.g.
 * `https://vansite.eu/de/l/new`), and the link field is not translated — so every
 * language would be sent to the German page.
 *
 * Only URLs resolving to a named app route are re-localized, because those paths are
 * identical in every language. Storyblok pages are deliberately excluded: their slugs
 * are translated (`/de/gastgeber-werden` vs `/en/become-host`), so swapping the locale
 * segment would point at a story that does not exist.
 *
 * @param {string} rawUrl - URL as authored in Storyblok
 * @param {string} locale - Current locale (e.g. "en")
 * @returns {string | null} Locale-prefixed path, or null when the URL must stay as authored
 */
export const localizeStoryblokUrl = (rawUrl, locale) => {
  if (typeof rawUrl !== 'string' || !rawUrl) return null;

  const isAbsolute = /^https?:\/\//i.test(rawUrl);
  if (isAbsolute && !INTERNAL_ORIGIN.test(rawUrl)) return null;

  const path = isAbsolute ? rawUrl.replace(INTERNAL_ORIGIN, '') : rawUrl;
  if (!path.startsWith('/')) return null;

  const suffixIndex = path.search(/[?#]/);
  const pathname = suffixIndex === -1 ? path : path.slice(0, suffixIndex);
  const suffix = suffixIndex === -1 ? '' : path.slice(suffixIndex);

  const matched = matchPathname(pathname, routeConfiguration())[0]?.route;
  if (!matched?.name || matched.name === 'StoryblokPage') return null;

  const withoutLocale = pathname.replace(
    new RegExp(`^/(${Object.keys(config.languages).join('|')})(?=/|$)`),
    ''
  );

  return `/${locale}${withoutLocale}${suffix}`;
};
